<template>
  <div class="flex-1 flex flex-col min-h-0">
    <div class="flex items-center justify-between mb-4">
      <h4 class="text-lg font-medium text-gray-800">
        Projections Stats ({{ stats.length }})
      </h4>
      <div class="flex items-center space-x-2">
        <!-- View Toggle -->
        <div class="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            @click="viewMode = 'side-by-side'"
            class="px-3 py-1 text-sm rounded-md transition-colors"
            :class="viewMode === 'side-by-side' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600 hover:text-gray-800'"
          >
            <i class="fas fa-columns mr-1"></i>
            Side by Side
          </button>
          <button
            @click="viewMode = 'single-table'"
            class="px-3 py-1 text-sm rounded-md transition-colors"
            :class="viewMode === 'single-table' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600 hover:text-gray-800'"
          >
            <i class="fas fa-table mr-1"></i>
            Single Table
          </button>
        </div>
        <!-- Search Input -->
        <div class="relative w-[700px]">
          <input
            :value="searchQuery"
            @input="handleSearchInput"
            @focus="showSearchResults = true"
            @blur="handleSearchBlur"
            @keydown="handleSearchKeydown"
            type="text"
            placeholder="Search stats..."
            class="px-4 w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base w-80 font-medium"
            style="min-height: 2.5rem; font-size: 1.1rem;"
          />
          <button
            @click="handleSearchButtonClick"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
            title="Search stats"
            :disabled="isSearching"
          >
            <i v-if="isSearching" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-search"></i>
          </button>
          
          <!-- Search Results Popup -->
          <SearchResultsPopup
            :is-visible="showSearchResults"
            :results="searchResults"
            :selected-index="selectedSearchIndex"
            :is-loading="isSearching"
            @select-result="handleResultSelection"
            @update:selected-index="selectedSearchIndex = $event"
          />
        </div>

      </div>
    </div>
    
    <!-- Projections Stats Tables -->
    <div class="flex-1 flex flex-col min-h-0">
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <div class="text-center text-gray-500">
          <i class="fas fa-spinner fa-spin text-3xl mb-4"></i>
          <p class="text-lg">Loading projections stats...</p>
        </div>
      </div>
      
      <div v-else-if="stats.length > 0" class="flex-1 flex flex-col min-h-0">
        <!-- Side by Side View -->
        <div v-if="viewMode === 'side-by-side'" class="flex-1 flex space-x-4 min-h-0">
          <!-- Left Table -->
          <div class="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col min-h-0">
            <div class="bg-blue-800 text-white px-4 py-3 flex-shrink-0">
              <h5 class="text-lg font-medium">{{ getDataTypeLabel(leftTableData[0]?.data_type) }} ({{ leftTableData.length }})</h5>
            </div>
            <div class="flex-1 overflow-x-auto min-h-0">
              <table class="w-full min-w-max">
                <thead class="bg-gray-100 sticky top-0">
                  <tr class="text-xs font-medium text-gray-700">
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('rank', 'left')">
                      # <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('league', 'left')">
                      League <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('team_name', 'left')">
                      Team <i class="fas fa-sort ml-1"></i>
                    </th>
                    <!-- INSERT_YOUR_CODE -->
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('season', 'left')">
                      Season <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('gp', 'left')">
                      GP <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('mpg', 'left')">
                      MPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ppg', 'left')">
                      PPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fgm', 'left')">
                      FGM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fga', 'left')">
                      FGA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fg_percent', 'left')">
                      FG% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_pm', 'left')">
                      3PM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_pa', 'left')">
                      3PA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_p_percent', 'left')">
                      3P% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ftm', 'left')">
                      FTM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fta', 'left')">
                      FTA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ft_percent', 'left')">
                      FT% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('orb', 'left')">
                      ORB <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('drb', 'left')">
                      DRB <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('rpg', 'left')">
                      RPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('apg', 'left')">
                      APG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('spg', 'left')">
                      SPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('bpg', 'left')">
                      BPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('tov', 'left')">
                      TOV <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('pf', 'left')">
                      PF <i class="fas fa-sort ml-1"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(stat, index) in sortedLeftTableData"
                    :key="stat.id"
                    class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  >
                    <td class="px-2 py-2 text-sm font-medium text-gray-700">{{ stat.rank }}</td>
                    <td class="px-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{{ stat.league }}</td>
                    <td class="px-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{{ stat.team_name }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ stat.season }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ stat.gp }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.mpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.ppg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fgm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fga) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.fg_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.three_pm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.three_pa) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.three_p_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.ftm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fta) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.ft_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.orb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.drb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.rpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.apg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.spg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.bpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.tov) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.pf) }}</td>
                  </tr>
                </tbody>
                                 <tfoot class="bg-gray-100">
                   <tr>
                     <td class="px-2 py-2 text-sm font-medium text-gray-700">Averages</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">-</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.gp) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.mpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.ppg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.fgm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.fga) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(leftTableAverages.fg_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.three_pm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.three_pa) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(leftTableAverages.three_p_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.ftm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.fta) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(leftTableAverages.ft_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.orb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.drb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.rpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.apg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.spg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.bpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.tov) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(leftTableAverages.pf) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Right Table -->
          <div class="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col min-h-0">
            <div class="bg-green-800 text-white px-4 py-3 flex-shrink-0">
              <h5 class="text-lg font-medium">{{ getDataTypeLabel(rightTableData[0]?.data_type) }} ({{ rightTableData.length }})</h5>
            </div>
            <div class="flex-1 overflow-x-auto min-h-0">
              <table class="w-full min-w-max">
                <thead class="bg-gray-100 sticky top-0">
                  <tr class="text-xs font-medium text-gray-700">
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('rank', 'right')">
                      # <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('league', 'right')">
                      League <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('team_name', 'right')">
                      Team <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('season', 'right')">
                      Season <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('gp', 'right')">
                      GP <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('mpg', 'right')">
                      MPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ppg', 'right')">
                      PPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fgm', 'right')">
                      FGM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fga', 'right')">
                      FGA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fg_percent', 'right')">
                      FG% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_pm', 'right')">
                      3PM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_pa', 'right')">
                      3PA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_p_percent', 'right')">
                      3P% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ftm', 'right')">
                      FTM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fta', 'right')">
                      FTA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ft_percent', 'right')">
                      FT% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('orb', 'right')">
                      ORB <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('drb', 'right')">
                      DRB <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('rpg', 'right')">
                      RPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('apg', 'right')">
                      APG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('spg', 'right')">
                      SPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('bpg', 'right')">
                      BPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('tov', 'right')">
                      TOV <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('pf', 'right')">
                      PF <i class="fas fa-sort ml-1"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(stat, index) in sortedRightTableData"
                    :key="stat.id"
                    class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  >
                    <td class="px-2 py-2 text-sm font-medium text-gray-700">{{ stat.rank }}</td>
                    <td class="px-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{{ stat.league }}</td>
                    <td class="px-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{{ stat.team_name }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ stat.season }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ stat.gp }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.mpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.ppg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fgm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fga) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.fg_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.three_pm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.three_pa) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.three_p_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.ftm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fta) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.ft_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.orb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.drb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.rpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.apg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.spg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.bpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.tov) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.pf) }}</td>
                  </tr>
                </tbody>
                                 <tfoot class="bg-gray-100">
                   <tr>
                     <td class="px-2 py-2 text-sm font-medium text-gray-700">Averages</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">-</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.gp) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.mpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.ppg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.fgm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.fga) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(rightTableAverages.fg_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.three_pm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.three_pa) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(rightTableAverages.three_p_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.ftm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.fta) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(rightTableAverages.ft_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.orb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.drb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.rpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.apg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.spg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.bpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.tov) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(rightTableAverages.pf) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <!-- Single Table View with Tabs -->
        <div v-else class="flex-1 flex flex-col min-h-0">
          <!-- Tabs -->
          <div class="bg-white border border-gray-200 rounded-t-lg flex-shrink-0">
            <div class="flex border-b border-gray-200">
              <button
                v-for="dataType in dataTypes"
                :key="dataType"
                @click="activeTab = dataType"
                class="px-6 py-3 text-sm font-medium border-b-2 transition-colors"
                :class="activeTab === dataType 
                  ? 'border-blue-500 text-blue-600 bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
              >
                {{ getDataTypeLabel(dataType) }} ({{ groupedStats[dataType]?.length || 0 }})
              </button>
            </div>
          </div>

          <!-- Single Table -->
          <div class="flex-1 bg-white border border-gray-200 border-t-0 rounded-b-lg overflow-hidden flex flex-col min-h-0">
            <div class="flex-1 overflow-x-auto min-h-0">
              <table class="w-full min-w-max">
                <thead class="bg-gray-100 sticky top-0">
                  <tr class="text-xs font-medium text-gray-700">
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('rank', 'single')">
                      # <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('league', 'single')">
                      League <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('team_name', 'single')">
                      Team <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" @click="sortBy('season', 'single')">
                      Season <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('gp', 'single')">
                      GP <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('mpg', 'single')">
                      MPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ppg', 'single')">
                      PPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fgm', 'single')">
                      FGM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fga', 'single')">
                      FGA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fg_percent', 'single')">
                      FG% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_pm', 'single')">
                      3PM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_pa', 'single')">
                      3PA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('three_p_percent', 'single')">
                      3P% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ftm', 'single')">
                      FTM <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('fta', 'single')">
                      FTA <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('ft_percent', 'single')">
                      FT% <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('orb', 'single')">
                      ORB <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('drb', 'single')">
                      DRB <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('rpg', 'single')">
                      RPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('apg', 'single')">
                      APG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('spg', 'single')">
                      SPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('bpg', 'single')">
                      BPG <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('tov', 'single')">
                      TOV <i class="fas fa-sort ml-1"></i>
                    </th>
                    <th class="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" @click="sortBy('pf', 'single')">
                      PF <i class="fas fa-sort ml-1"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(stat, index) in sortedSingleTableData"
                    :key="stat.id"
                    class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  >
                    <td class="px-2 py-2 text-sm font-medium text-gray-700">{{ stat.rank }}</td>
                    <td class="px-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{{ stat.league }}</td>
                    <td class="px-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{{ stat.team_name }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ stat.season }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ stat.gp }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.mpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.ppg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fgm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fga) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.fg_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.three_pm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.three_pa) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.three_p_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.ftm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.fta) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(stat.ft_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.orb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.drb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.rpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.apg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.spg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.bpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.tov) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(stat.pf) }}</td>
                  </tr>
                </tbody>
                                 <tfoot class="bg-gray-100">
                   <tr>
                     <td class="px-2 py-2 text-sm font-medium text-gray-700">Averages</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">-</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">-</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">-</td>
                     <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.gp) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.mpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.ppg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.fgm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.fga) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(singleTableAverages.fg_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.three_pm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.three_pa) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(singleTableAverages.three_p_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.ftm) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.fta) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatPercentage(singleTableAverages.ft_percent) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.orb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.drb) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.rpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.apg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.spg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.bpg) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.tov) }}</td>
                    <td class="px-2 py-2 text-sm text-center text-gray-700">{{ formatNumber(singleTableAverages.pf) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="flex-1 flex items-center justify-center min-h-0">
        <div class="text-center text-gray-500">
          <i class="fas fa-chart-bar text-3xl mb-4"></i>
          <p class="text-lg">No projections stats found</p>
          <p class="text-sm mt-1">Stats will appear here when available</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SearchResultsPopup from './SearchResultsPopup.vue'
import axios from '../axios.js'

export default {
  name: 'ProjectionsStatsDisplay',
  components: {
    SearchResultsPopup
  },
  props: {
    stats: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    },
    currentLeague: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      viewMode: 'single-table', // 'side-by-side' or 'single-table'
      activeTab: null,
      leftSortField: 'rank',
      leftSortDirection: 'asc',
      rightSortField: 'rank',
      rightSortDirection: 'asc',
      singleSortField: 'rank',
      singleSortDirection: 'asc',
      // Search functionality
      searchResults: [],
      showSearchResults: false,
      selectedSearchIndex: -1,
      searchTimeout: null,
      isSearching: false
    }
  },
  computed: {
    groupedStats() {
      const grouped = {}
      this.stats.forEach(stat => {
        if (!grouped[stat.data_type]) {
          grouped[stat.data_type] = []
        }
        grouped[stat.data_type].push(stat)
      })
      return grouped
    },
    dataTypes() {
      return Object.keys(this.groupedStats)
    },
    leftTableData() {
      const dataTypes = this.dataTypes
      return dataTypes.length > 0 ? this.groupedStats[dataTypes[0]] || [] : []
    },
    rightTableData() {
      const dataTypes = this.dataTypes
      return dataTypes.length > 1 ? this.groupedStats[dataTypes[1]] || [] : []
    },
    sortedLeftTableData() {
      return this.sortData(this.leftTableData, this.sortField, this.sortDirection)
    },
    sortedRightTableData() {
      return this.sortData(this.rightTableData, this.sortField, this.sortDirection)
    },
    sortedSingleTableData() {
      return this.sortData(this.groupedStats[this.activeTab] || [], this.sortField, this.sortDirection)
    },
    // Average calculations for left table
    leftTableAverages() {
      if (this.leftTableData.length === 0) return {}
      return this.calculateAverages(this.leftTableData)
    },
    // Average calculations for right table
    rightTableAverages() {
      if (this.rightTableData.length === 0) return {}
      return this.calculateAverages(this.rightTableData)
    },
    // Average calculations for single table
    singleTableAverages() {
      const currentData = this.groupedStats[this.activeTab] || []
      if (currentData.length === 0) return {}
      return this.calculateAverages(currentData)
    }
  },
  watch: {
    dataTypes: {
      immediate: true,
      handler(newTypes) {
        if (newTypes.length > 0 && !this.activeTab) {
          // Default to opponent_totals if available, otherwise first available type
          this.activeTab = newTypes.includes('opponent_totals') ? 'opponent_totals' : newTypes[0]
        }
      }
    },
    currentLeague: {
      immediate: true,
      handler(newLeague) {
        if (newLeague && newLeague.id) {
          this.fetchLeagueStats()
        }
      }
    }
  },
  mounted() {
    // Automatically fetch league stats when component is mounted
    this.fetchLeagueStats()
  },
  beforeUnmount() {
    // Clean up search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  emits: ['toggle-visibility', 'search-stats', 'update:searchQuery'],
  methods: {
    // Search functionality methods
    async handleSearchInput(event) {
      const query = event.target.value
      this.$emit('update:searchQuery', query)
      
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      
      // Clear results if query is empty
      if (!query.trim()) {
        this.searchResults = []
        this.showSearchResults = false
        return
      }
      
      // Debounce the search
      this.searchTimeout = setTimeout(() => {
        this.performFuzzySearch(query)
      }, 300)
    },
    
    async performFuzzySearch(query) {
      if (!query.trim() || this.isSearching) return
      
      this.isSearching = true
      try {
        const response = await axios.get(`/api/v2/projections-leagues/all-leagues-stats/fuzzy-search/${encodeURIComponent(query)}`)
        this.searchResults = response.data || []
        this.showSearchResults = true
        this.selectedSearchIndex = -1
      } catch (error) {
        console.error('Fuzzy search error:', error)
        this.searchResults = []
      } finally {
        this.isSearching = false
      }
    },
    
    async performStrictSearch() {
      const query = this.searchQuery.trim()
      if (!query) return
      
      try {
        const response = await axios.post(`/api/v2/projections-leagues/all-leagues-stats/search`, {
          current_league: this.currentLeague,
          search_league: query
        })
        this.$emit('search-stats', response.data || [])
        this.showSearchResults = false
        this.searchResults = []
      } catch (error) {
        console.error('Strict search error:', error)
        this.$emit('search-stats', [])
      }
    },
    
    async performStrictSearchWithLeague(selectedLeague) {
      try {
        const response = await axios.post(`/api/v2/projections-leagues/all-leagues-stats/search`, {
          current_league: this.currentLeague,
          selected_league: {
            league_id: selectedLeague.league_id || selectedLeague.id,
            league_name: selectedLeague.league_name || selectedLeague.league,
            team_name: selectedLeague.team_name,
            season: selectedLeague.season,
            data_type: selectedLeague.data_type
          }
        })
        
        // Emit the search results to parent component
        this.$emit('search-stats', response.data || [])
        this.showSearchResults = false
        this.searchResults = []
      } catch (error) {
        console.error('Strict search with league error:', error)
        // Emit empty array on error to clear results
        this.$emit('search-stats', [])
      }
    },
    
    handleSearchButtonClick() {
      // If there's a selected result, use that, otherwise perform regular search
      if (this.selectedSearchIndex >= 0 && this.selectedSearchIndex < this.searchResults.length) {
        this.handleResultSelection(this.searchResults[this.selectedSearchIndex])
      } else {
        this.performStrictSearch()
      }
    },
    
    handleResultSelection(result) {
      // Update the search query with the selected result
      this.$emit('update:searchQuery', result.league)
      this.showSearchResults = false
      this.searchResults = []
      
      // Send POST request with selected league data
      this.performStrictSearchWithLeague(result)
    },
    
    handleSearchBlur() {
      // Delay hiding to allow for click events on results
      setTimeout(() => {
        this.showSearchResults = false
      }, 200)
    },
    
    handleSearchKeydown(event) {
      if (!this.showSearchResults || this.searchResults.length === 0) return
      
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.selectedSearchIndex = Math.min(this.selectedSearchIndex + 1, this.searchResults.length - 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          this.selectedSearchIndex = Math.max(this.selectedSearchIndex - 1, -1)
          break
        case 'Enter':
          event.preventDefault()
          if (this.selectedSearchIndex >= 0 && this.selectedSearchIndex < this.searchResults.length) {
            this.handleResultSelection(this.searchResults[this.selectedSearchIndex])
          } else {
            this.performStrictSearch()
          }
          break
        case 'Escape':
          this.showSearchResults = false
          this.selectedSearchIndex = -1
          break
      }
    },
    
    async fetchLeagueStats() {
      if (!this.currentLeague || !this.currentLeague.id) {
        console.log('No current league available for fetching stats')
        return
      }
      
      try {
        const response = await axios.get(`/api/v2/projections-leagues/all-leagues-stats/league/${this.currentLeague.id}`)
        console.log('League stats fetched:', response.data)
        this.$emit('search-stats', response.data || [])
      } catch (error) {
        console.error('Error fetching league stats:', error)
        this.$emit('search-stats', [])
      }
    },
    formatPercentage(value) {
      if (value === null || value === undefined) return '0.0%'
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(numValue)) return '0.0%'
      return (numValue * 100).toFixed(2) + '%'
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '0'
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(numValue)) return '0'
      return numValue.toFixed(2)
    },
    sortData(data, field, direction) {
      if (!data.length) return []
      
      return [...data].sort((a, b) => {
        let aVal = a[field]
        let bVal = b[field]
        
        // Convert to numbers for numeric fields
        if (typeof aVal === 'string' && !isNaN(parseFloat(aVal))) {
          aVal = parseFloat(aVal)
        }
        if (typeof bVal === 'string' && !isNaN(parseFloat(bVal))) {
          bVal = parseFloat(bVal)
        }
        
        if (aVal < bVal) return direction === 'asc' ? -1 : 1
        if (aVal > bVal) return direction === 'asc' ? 1 : -1
        return 0
      })
    },
    sortBy(field, table) {
      if (table === 'left') {
        if (this.leftSortField === field) {
          this.leftSortDirection = this.leftSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
          this.leftSortField = field
          this.leftSortDirection = 'asc'
        }
      } else if (table === 'right') {
        if (this.rightSortField === field) {
          this.rightSortDirection = this.rightSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
          this.rightSortField = field
          this.rightSortDirection = 'asc'
        }
      } else if (table === 'single') {
        if (this.singleSortField === field) {
          this.singleSortDirection = this.singleSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
          this.singleSortField = field
          this.singleSortDirection = 'asc'
        }
      }
    },
    getDataTypeLabel(dataType) {
      const labels = {
        'team_totals': 'Team Totals',
        'opponent_totals': 'Opponent Totals',
        'player_totals': 'Player Totals',
        'team_averages': 'Team Averages',
        'opponent_averages': 'Opponent Averages',
        'player_averages': 'Player Averages'
      }
      return labels[dataType] || dataType || 'Unknown Type'
    },
    calculateAverages(data) {
      if (!data || data.length === 0) return {}
      
      const averages = {}
      const count = data.length
      
      // Helper function to safely parse and sum numeric values
      const safeSum = (data, field) => {
        return data.reduce((sum, stat) => {
          const value = stat[field]
          if (value === null || value === undefined) return sum
          const numValue = typeof value === 'string' ? parseFloat(value) : value
          return sum + (isNaN(numValue) ? 0 : numValue)
        }, 0)
      }
      
      // Calculate sums for all columns
      const sums = {
        rank: safeSum(data, 'rank'),
        gp: safeSum(data, 'gp'),
        mpg: safeSum(data, 'mpg'),
        ppg: safeSum(data, 'ppg'),
        fgm: safeSum(data, 'fgm'),
        fga: safeSum(data, 'fga'),
        fg_percent: safeSum(data, 'fg_percent'),
        three_pm: safeSum(data, 'three_pm'),
        three_pa: safeSum(data, 'three_pa'),
        three_p_percent: safeSum(data, 'three_p_percent'),
        ftm: safeSum(data, 'ftm'),
        fta: safeSum(data, 'fta'),
        ft_percent: safeSum(data, 'ft_percent'),
        orb: safeSum(data, 'orb'),
        drb: safeSum(data, 'drb'),
        rpg: safeSum(data, 'rpg'),
        apg: safeSum(data, 'apg'),
        spg: safeSum(data, 'spg'),
        bpg: safeSum(data, 'bpg'),
        tov: safeSum(data, 'tov'),
        pf: safeSum(data, 'pf')
      }
      
      // Calculate averages for all columns
      Object.keys(sums).forEach(key => {
        averages[key] = sums[key] / count
      })
      
      return averages
    }
  }
}
</script>

<style scoped>
/* Add any custom styles here */
</style>
